import { IAssessment } from "@/modules/assessments/assessments.types";
import { TTestAnswer } from "@/shared/hooks/initializer/use-initialize-candidate.hook";
import { QuestionTypes } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { ChartCategories } from "@/shared/components/charts/types/chartTypes";

// TODO: GET RID of duplicated interfaces
export enum LanguagesIsoEnum {
  enUS = "en-US",
  esUS = "es-US",
}

// Mirrors the BE ResolvedLocalizedContent envelope: a candidate-facing value resolved to one
// locale. `locale` is the language actually returned (the requested one, or a fallback).
export interface ResolvedLocalizedContent<T = string> {
  value: T | null;
  locale: LanguagesIsoEnum;
}

export interface ITestGroupHeader {
  testId: number;
  groupHeaderText: ResolvedLocalizedContent;
}

interface LocalizedLanguageText {
  [key: string]: string | undefined;
}

export enum ChartCategoryEnum {
  BaS = "BehaviorsAndSkills",
  MS = "Motivators",
}
export interface ITestConfig {
  suggestedTime: number;
  questionCount: number;
  backButton: boolean;
  autoAdvance: boolean;
  chartCategory: ChartCategoryEnum;
  competenciesName?: [string, string];
  groupSize?: number;
  groupHeaderText?: LocalizedLanguageText;
}

export interface IClientTest {
  testId: number;
  name: string;
  internalName?: string;
  config: Pick<ITestConfig, "suggestedTime" | "questionCount">;
}

export interface ITest {
  createdAt: string;
  updatedAt: string;
  testId: number;
  key?: number;
  name: string;
  internalName?: string;
  config: ITestConfig;
  clientId: number;
  testElements: ITestElement[];
  testAnswers: TTestAnswer[];
  assessmentTests: IAssessment[];
  /** Locales this test is offered in (always at least one). Set by translated-content import. */
  availableLocales?: LanguagesIsoEnum[];
}

export interface IAssessmentTest {
  createdAt?: string;
  updatedAt?: string;
  assessmentTestId: number;
  rank: number;
  normId: number;
  assessmentId: number;
  testId: number;
  config: { competencies: { [key: string]: boolean | 0 | 1 | 2 | 3 | 4 } };
  test: {
    createdAt: string;
    updatedAt: string;
    testId: number;
    name: string;
    internalName?: string;
    reportColor: string | null;
    testElements: ITestElement[];
    config: {
      suggestedTime: number;
      chartCategory: ChartCategories;
      competenciesName?: [string, string];
    };
    clientId: number;
    includeInOverallScore?: boolean;
  };
}

export type ISectionConfig = {
  name: string;
  timeLimit?: number; // in minutes. if null, then section is untimed
};

export interface ITestElement {
  createdAt: string;
  updatedAt: string;
  elementId: number;
  testId: number;
  rank: number;
  config: {
    key: string;
    type: QuestionTypes;
    breakAfter: boolean;
    payload: {
      choices: IChoice[];
      question: IQuestion;
    } & { body: IBody };
    required?: boolean;
    /**
     * @description
     * Only in case when element starts Section
     *
     * Section - it's an abstraction that gives opportunity to divide group of elements and make some logic (i.e. timing)
     * */
    startsSection?: ISectionConfig;
  };
  questionNumber?: number;
}

export interface IQuestion {
  [key: string]: string | [string, string];
}

// A single question's content in one locale (mirrors the BE IQuestion type).
export type IQuestionValue = string | [string, string];

// Test element as returned by GET invites/:inviteId/questionsAndAnswers: question/body are
// resolved-locale envelopes instead of the locale-keyed maps stored on the test entity.
export interface ILocalizedTestElement extends Omit<ITestElement, "config"> {
  config: Omit<ITestElement["config"], "payload"> & {
    payload: {
      choices: IChoice[];
      question?: ResolvedLocalizedContent<IQuestionValue>;
      body?: ResolvedLocalizedContent<IQuestionValue>;
    };
  };
}

export interface IBody {
  [key: string]: string;
}

export interface IChoice {
  key: string;
  choice: boolean | string;
  scales: (string | IScaleDetail)[];

  // Server-resolved translation of this choice's display text, for the candidate's locale. Only
  // present on questions with dynamic, question-specific options; question types whose options are
  // hardcoded on the FE don't carry it.
  label?: ResolvedLocalizedContent<string>;

  // Client-side display override.
  customName?: string;
}

export interface IScaleDetail {
  abbr: string;
  points: number;
}
